import imgGlobalTeam from '@assets/img/bg-about-global-team.webp'
import imgCustomerValue from '@assets/img/bg-about-customer-value-img.webp'
import CasperHeroImg from '@assets/img/about-casper.webp'
import Image from 'next/image'
import { useState } from 'react'
import OurLeadership from '@modules/company/components/OurLeadership/OurLeadership'
import { dehydrate, QueryClient } from 'react-query'
import { getLegalDocs } from 'src/@shared/service/cms-api'
import useTeamList from '@modules/teams/hooks/useTeamList'
import AboutFounders from '@modules/common/components/Founders/AboutFounders'

import CustomHead from '@atk/components/head/customHead/customHead'
import Newsletter2 from '@atk/components/newsletter/Newsletter2/Newsletter2'
import Hero from '@atk/components/hero/Hero/Hero'
import CardHorizontal4 from '@atk/components/cards/CardHorizontal4/CardHorizontal4'

import FramerMotion from '@atk/hooks/framerMotion'

const initialArgs = { sort: 'position', limit: 0, page: 1 }

export async function getServerSideProps() {
    const queryClient = new QueryClient()
    // prefetch data on the server
    await queryClient.fetchQuery(['legaldocs'], () => getLegalDocs(initialArgs))
    return {
        props: {
            // dehydrate query cache
            dehydratedState: dehydrate(queryClient),
        },
    }
}

export default function About() {
    const CardHorizontal4Items = [
        {
            title: 'Act like an Owner',
            desc: 'This is our company. We each are and act like owners. We are working for ourselves. In doing so, we are humble and quietly confident.',
            bgColor: '#BCFC07',
        },
        {
            title: 'Appeal to Competence',
            desc: 'We listen attentively, speak candidly, and treat others respectfully. Our words are important to communicate ideas and create synergies, but our actions speak the loudest. We lead by example.',
            bgColor: '#AD53E5',
        },
        {
            title: 'Have Fortitude',
            desc: 'Disagree & Align: We have convictions and are tenacious. We respectfully challenge ideas and positions. Once a decision is made, we march forth together.',
            bgColor: '#4589F6',
        },
        {
            title: 'Commit to Excellence',
            desc: 'We consistently exceed expectations, not just for ourselves but for each other. We focus on results over process. We don’t just meet the bar; we raise the bar.',
            bgColor: '#F622E6',
        },
        {
            title: 'Learn and Grow',
            desc: 'We choose to evolve. When doing so, we know that mistakes do happen. We recognize that discomfort is a part of the growing process, and we do not shy away from it.',
            bgColor: '#3EDC64',
        },
        {
            title: 'Hire and Develop the Best',
            desc: 'We recognize and develop exceptional talent. We embrace hiring people smarter than ourselves. Our team is our most valuable asset.',
            bgColor: '#F9AACA',
        },
    ]

    const { founders, executives } = useTeamList()
    const [showDesc, setShowDesc] = useState(false)
    const handleReadMore = () => setShowDesc(true)
    const pageClass = `aboutUsPage`
    return (
        <>
            <CustomHead
                title="About Us & The Casper Team | Casper Labs"
                description="Learn about Casper Labs, a leader in blockchain technologies, is revolutionizing industries. Casper is the first blockchain built for business adoption."
            >
                <link rel="preload" href={CasperHeroImg.src} as="image" />
            </CustomHead>
            <FramerMotion animation="fadeIn">
                <div className={pageClass}>
                    <main className="aboutUsPageMain">
                        <Hero
                            customClass="pt-0 pb-0 header"
                            isHeader
                            bgImage={CasperHeroImg.src}
                            col1={6}
                            col2={6}
                            halfTitle="About Us"
                            title='A <span class="text-green">blockchain</span> that makes sense for business'
                            desc="Casper is the first blockchain built specifically for business adoption. Casper’s unique design allows it to adapt and endure with your business over time – and thanks to Casper Labs, organizations building on Casper always have a clear place to turn for dedicated services, support and bespoke software development."
                        ></Hero>
                        <div className="position-relative aboutGradient">
                            <FramerMotion>
                                <Image
                                    className="customerValueImg"
                                    src={imgCustomerValue.src}
                                    alt="Casper Labs"
                                    width={800}
                                    height={800}
                                />
                                <Hero
                                    customClass="mt-0 pt-0 pb-lg-5 customerValue"
                                    colLg1={8}
                                    colLg2={4}
                                    col1={8}
                                    col2={4}
                                    childrenCol={1}
                                    title='We’re <span class="text-green">building</span> the essential foundation for an entirely new era of <span class="text-green">customer value</span> and business success.'
                                >
                                    {!showDesc && (
                                        <p className="mb-5 btn btn-primary customerValueDesc" onClick={handleReadMore}>
                                            Read More
                                        </p>
                                    )}
                                    {showDesc && (
                                        <p className="mb-5">
                                            Casper Labs’ leadership team comes from enterprise technology backgrounds.
                                            We possess a deep understanding of what organizations need to succeed with
                                            emerging technology. Casper provides an unprecedented degree of
                                            customization and control than is possible on other, one-size-fits-all
                                            blockchains.
                                        </p>
                                    )}
                                </Hero>
                            </FramerMotion>
                            {founders && (
                                <div className="position-relative">
                                    <FramerMotion>
                                        <AboutFounders founders={founders.members} />
                                    </FramerMotion>
                                </div>
                            )}
                        </div>
                        {executives && (
                            <FramerMotion>
                                <OurLeadership list={executives.members} />
                            </FramerMotion>
                        )}
                        <FramerMotion>
                            <Hero
                                customClass="mt-0 pt-0 pb-0 ceoMessage"
                                col1={11}
                                col2={1}
                                title="<blockquote>We realized that neither on the structure side nor the product side was a blockchain company or protocol that was really taking care of the needs of enterprise...that's what led me to found Casper Labs.</blockquote>"
                                desc="Mrinal Manohar, CEO"
                            ></Hero>
                        </FramerMotion>
                        <FramerMotion>
                            <Hero
                                customClass="mt-0 pt-0 pb-0 globalTeam"
                                colLg1={6}
                                colLg2={6}
                                title="We are one global team"
                                desc="Our growing team of nearly 100 employees spans three continents and countless time zones. We’re committed to building the most reliable and sustainable blockchain for businesses by meeting organizations where they are, not vice versa."
                                linkButton={{ label: 'Join our team', link: '/careers', variant: 'primary' }}
                                bgImage={imgGlobalTeam.src}
                            ></Hero>
                        </FramerMotion>
                        <FramerMotion>
                            <CardHorizontal4
                                hasArrow
                                customClass="ourValuesSection pt-8 pb-7"
                                sectionTitle="Our Values"
                                sectionDesc="We enjoy a low turnover rate and high employee satisfaction due to our core values - we stay true to them through everything that we do."
                                items={CardHorizontal4Items}
                            />
                        </FramerMotion>
                        <Newsletter2 col1={5} col2={7} title="Sign up for our newsletter!" />
                    </main>
                </div>
            </FramerMotion>
        </>
    )
}
